/**
 * K3 Revenue Split Section
 *
 * Nettoomsättningens fördelning enligt K3 kapitel 5
 * Uppdelning per verksamhetsgren och geografisk marknad
 */

import { useState, useEffect } from "react";
import { Plus, Trash2, PieChart, Globe, Briefcase, AlertTriangle } from "lucide-react";
import { formatNumber } from "@/lib/format";
import type { UserInput } from "@/lib/api";

interface RevenueSplitItem {
  id: string;
  name: string;
  amount: number;
  percentage: number;
}

interface K3RevenueSplitSectionProps {
  userInput: UserInput;
  totalRevenue: number;
  onChange: (revenueSplit: UserInput["revenueSplit"]) => void;
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

export function K3RevenueSplitSection({
  userInput,
  totalRevenue,
  onChange,
}: K3RevenueSplitSectionProps) {
  // Business area split
  const [businessAreas, setBusinessAreas] = useState<RevenueSplitItem[]>(() => {
    if (userInput.revenueSplit?.byBusinessArea?.length) {
      return userInput.revenueSplit.byBusinessArea.map((item) => ({
        id: generateId(),
        name: item.name,
        amount: item.amount,
        percentage: item.percentage,
      }));
    }
    // Default: single business area with all revenue
    return [{ id: generateId(), name: "Huvudverksamhet", amount: totalRevenue, percentage: 100 }];
  });

  // Geographic split
  const [geographicMarkets, setGeographicMarkets] = useState<RevenueSplitItem[]>(() => {
    if (userInput.revenueSplit?.byGeography?.length) {
      return userInput.revenueSplit.byGeography.map((item) => ({
        id: generateId(),
        name: item.region,
        amount: item.amount,
        percentage: item.percentage,
      }));
    }
    // Default: Sweden only
    return [{ id: generateId(), name: "Sverige", amount: totalRevenue, percentage: 100 }];
  });

  // Sync changes to parent
  useEffect(() => {
    const revenueSplit: UserInput["revenueSplit"] = {
      byBusinessArea: businessAreas.map((item) => ({
        name: item.name,
        amount: item.amount,
        percentage: item.percentage,
      })),
      byGeography: geographicMarkets.map((item) => ({
        region: item.name,
        amount: item.amount,
        percentage: item.percentage,
      })),
    };
    onChange(revenueSplit);
  }, [businessAreas, geographicMarkets]);

  // Calculate totals and check if balanced
  const businessAreaTotal = businessAreas.reduce((sum, item) => sum + item.amount, 0);
  const geographicTotal = geographicMarkets.reduce((sum, item) => sum + item.amount, 0);
  const businessAreaBalanced = Math.abs(businessAreaTotal - totalRevenue) < 1;
  const geographicBalanced = Math.abs(geographicTotal - totalRevenue) < 1;

  // Add new business area
  const addBusinessArea = () => {
    setBusinessAreas([...businessAreas, { id: generateId(), name: "", amount: 0, percentage: 0 }]);
  };

  // Update business area
  const updateBusinessArea = (id: string, field: keyof RevenueSplitItem, value: string | number) => {
    setBusinessAreas(
      businessAreas.map((item) => {
        if (item.id !== id) return item;
        const updated = { ...item, [field]: value };
        // Recalculate percentage when amount changes
        if (field === "amount" && totalRevenue > 0) {
          updated.percentage = Math.round((Number(value) / totalRevenue) * 1000) / 10;
        }
        return updated;
      })
    );
  };

  // Remove business area
  const removeBusinessArea = (id: string) => {
    if (businessAreas.length > 1) {
      setBusinessAreas(businessAreas.filter((item) => item.id !== id));
    }
  };

  // Add new geographic market
  const addGeographicMarket = () => {
    setGeographicMarkets([...geographicMarkets, { id: generateId(), name: "", amount: 0, percentage: 0 }]);
  };

  // Update geographic market
  const updateGeographicMarket = (id: string, field: keyof RevenueSplitItem, value: string | number) => {
    setGeographicMarkets(
      geographicMarkets.map((item) => {
        if (item.id !== id) return item;
        const updated = { ...item, [field]: value };
        // Recalculate percentage when amount changes
        if (field === "amount" && totalRevenue > 0) {
          updated.percentage = Math.round((Number(value) / totalRevenue) * 1000) / 10;
        }
        return updated;
      })
    );
  };

  // Remove geographic market
  const removeGeographicMarket = (id: string) => {
    if (geographicMarkets.length > 1) {
      setGeographicMarkets(geographicMarkets.filter((item) => item.id !== id));
    }
  };

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
            <PieChart className="text-emerald-600" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Nettoomsättningens fördelning</h3>
            <p className="text-sm text-gray-500">K3 kap 5.3, ÅRL 5:2</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
          K3
        </span>
      </div>

      {/* Info */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
        <p className="text-sm text-emerald-800">
          K3-företag ska i not ange nettoomsättningens fördelning på <strong>rörelsegrenar</strong> och{" "}
          <strong>geografiska marknader</strong> om dessa avviker väsentligt.
        </p>
      </div>

      {/* Total revenue reference */}
      <div className="bg-gray-50 rounded-lg p-3 mb-6">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Total nettoomsättning (från resultaträkningen)</span>
          <span className="font-bold text-gray-900">{formatNumber(totalRevenue)} kr</span>
        </div>
      </div>

      {/* Business Area Split */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Briefcase size={16} className="text-gray-500" />
          <h4 className="font-medium text-gray-700">Per rörelsegren/verksamhetsområde</h4>
        </div>

        <div className="space-y-2">
          {businessAreas.map((item) => (
            <div key={item.id} className="flex items-center gap-2">
              <input
                type="text"
                value={item.name}
                onChange={(e) => updateBusinessArea(item.id, "name", e.target.value)}
                placeholder="Verksamhetsområde"
                className="input flex-1"
              />
              <div className="relative">
                <input
                  type="number"
                  value={item.amount || ""}
                  onChange={(e) => updateBusinessArea(item.id, "amount", Number(e.target.value) || 0)}
                  placeholder="0"
                  className="input w-32 pr-8 text-right"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">kr</span>
              </div>
              <span className="w-16 text-right text-sm text-gray-500">{item.percentage.toFixed(1)}%</span>
              <button
                type="button"
                onClick={() => removeBusinessArea(item.id)}
                disabled={businessAreas.length <= 1}
                className="p-1.5 text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-3">
          <button
            type="button"
            onClick={addBusinessArea}
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
          >
            <Plus size={16} />
            Lägg till verksamhetsområde
          </button>
          <div className={`text-sm font-medium ${businessAreaBalanced ? "text-green-600" : "text-amber-600"}`}>
            Summa: {formatNumber(businessAreaTotal)} kr
            {!businessAreaBalanced && (
              <span className="ml-2 text-amber-600">
                (differens: {formatNumber(Math.abs(businessAreaTotal - totalRevenue))} kr)
              </span>
            )}
          </div>
        </div>

        {!businessAreaBalanced && (
          <div className="flex items-center gap-2 mt-2 text-sm text-amber-600">
            <AlertTriangle size={14} />
            <span>Summan av verksamhetsområden bör motsvara total nettoomsättning</span>
          </div>
        )}
      </div>

      {/* Geographic Split */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Globe size={16} className="text-gray-500" />
          <h4 className="font-medium text-gray-700">Per geografisk marknad</h4>
        </div>

        <div className="space-y-2">
          {geographicMarkets.map((item) => (
            <div key={item.id} className="flex items-center gap-2">
              <input
                type="text"
                value={item.name}
                onChange={(e) => updateGeographicMarket(item.id, "name", e.target.value)}
                placeholder="Region/land"
                className="input flex-1"
              />
              <div className="relative">
                <input
                  type="number"
                  value={item.amount || ""}
                  onChange={(e) => updateGeographicMarket(item.id, "amount", Number(e.target.value) || 0)}
                  placeholder="0"
                  className="input w-32 pr-8 text-right"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">kr</span>
              </div>
              <span className="w-16 text-right text-sm text-gray-500">{item.percentage.toFixed(1)}%</span>
              <button
                type="button"
                onClick={() => removeGeographicMarket(item.id)}
                disabled={geographicMarkets.length <= 1}
                className="p-1.5 text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-3">
          <button
            type="button"
            onClick={addGeographicMarket}
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
          >
            <Plus size={16} />
            Lägg till geografisk marknad
          </button>
          <div className={`text-sm font-medium ${geographicBalanced ? "text-green-600" : "text-amber-600"}`}>
            Summa: {formatNumber(geographicTotal)} kr
            {!geographicBalanced && (
              <span className="ml-2 text-amber-600">
                (differens: {formatNumber(Math.abs(geographicTotal - totalRevenue))} kr)
              </span>
            )}
          </div>
        </div>

        {!geographicBalanced && (
          <div className="flex items-center gap-2 mt-2 text-sm text-amber-600">
            <AlertTriangle size={14} />
            <span>Summan av geografiska marknader bör motsvara total nettoomsättning</span>
          </div>
        )}
      </div>

      {/* Tip */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <p className="text-xs text-gray-500">
          <strong>Tips:</strong> Om all omsättning kommer från en verksamhetsgren och en marknad (t.ex. Sverige)
          behöver du inte lägga till fler. Uppdelningen behövs endast om verksamheten har väsentligt olika
          rörelsegrenar eller geografiska marknader.
        </p>
      </div>
    </div>
  );
}
